import { useFeatureFlag } from '@harnessio/ff-react-client-sdk';
import { OktaAuth } from '@okta/okta-auth-js';
import { Security } from '@okta/okta-react';
import { useEffect } from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { Client } from 'adapters/types';
import { REACT_QUERY_OPTIONS } from 'common/constants';
import { LocaleProvider } from 'components/LocalProvider';
import { store } from 'store';
import { theme } from 'utils';
import {
  oktaVariablesNoIAM,
  restoreOriginalUri,
  oktaIAMVariables
} from 'utils/OktaUtils';
import { AmplitudeProvider } from './components/AmplitudeProvider';
import { ClientProvider } from './components/ClientProvider';

const { REFETCH_ON_WINDOW_FOCUS, REFETCH_ON_MOUNT, RETRY, STALE_TIME } =
  REACT_QUERY_OPTIONS;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: REFETCH_ON_WINDOW_FOCUS,
      refetchOnMount: REFETCH_ON_MOUNT,
      retry: RETRY,
      staleTime: STALE_TIME
    }
  }
});

export interface AppProps {
  client: Client;
  children: React.ReactNode;
}

const App = ({ client, children }: AppProps): React.JSX.Element => {
  const harnessHAFlag = useFeatureFlag('iam_high_availability_enabled');
  const jwtFlag = useFeatureFlag('dms_mod_accounting_jwt_token_enable');

  useEffect(() => {
    if (typeof jwtFlag === 'boolean') {
      localStorage.setItem('JWT_FLAG', String(jwtFlag));
    }
  }, [jwtFlag]);

  return (
    <BrowserRouter>
      <Security
        oktaAuth={
          harnessHAFlag
            ? new OktaAuth(oktaIAMVariables(client.isMFE))
            : new OktaAuth(oktaVariablesNoIAM(client.isMFE))
        }
        restoreOriginalUri={restoreOriginalUri}
      >
        <Provider store={store}>
          <QueryClientProvider client={queryClient}>
            <LocaleProvider>
              <ThemeProvider theme={theme}>
                <ClientProvider client={client} jwtFlag={jwtFlag}>
                  <AmplitudeProvider>{children}</AmplitudeProvider>
                </ClientProvider>
              </ThemeProvider>
            </LocaleProvider>
          </QueryClientProvider>
        </Provider>
      </Security>
    </BrowserRouter>
  );
};

export default App;
